import { type ChangeEventHandler, useEffect, useState } from 'react'

import PlusIcon from '@/assets/icons/icon-plus.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button'
import { TextField } from '@/components/ui/text-field'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs'
import ChevronDownIcon from '@/modules/chatting/icons/chevron-down.svg'
import MessageIcon from '@/modules/chatting/icons/message.svg'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { useCollectionContext } from '../CollectionProvider'
import type { CollectionsItem } from '../CollectionsList/types'
import { AddUsers } from './AddUsers/AddUsers'
import { CollectionActions } from './CollectionActions'
import { CollectionUsers } from './CollectionUsers/CollectionUsers'
import { CollectionUsersFilters } from './CollectionUsersFilters/CollectionUsersFilters'
import { getListIdToSendMessage } from './utils'

interface Props {
  list?: CollectionsItem
  className?: string
  onCloseSelectedList: () => void
}

export const CollectionInfo = ({
  list,
  className,
  onCloseSelectedList,
}: Props) => {
  const {
    query,
    sort,
    order,
    setQuery,
    setSort,
    setOrder,
    orderOptions,
    sortOptions,
    filters,
    setFilters,
  } = useCollectionContext()
  const [addingUsers, setAddingUsers] = useState(false)
  const [queryValue, setQueryValue] = useState(query)

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setQueryValue(event.target.value)
    setQuery(event.target.value)
  }

  useEffect(() => {
    setQueryValue(query)
  }, [query])

  const onClickAddUsers = () => {
    setAddingUsers(true)
  }

  useEffect(() => {
    setAddingUsers(false)
  }, [list?.id])

  const noListView = (
    <div className="flex h-full flex-col items-center justify-center space-y-4">
      <NotFoundImage
        width={80}
        height={80}
      />
      <div className="text-sm leading-6 text-secondary-300">No users yet</div>
    </div>
  )

  const noUsersView = (
    <>
      {list?.type === 'custom' && (
        <Button
          variant="outline"
          className="w-full"
          onClick={onClickAddUsers}
        >
          <PlusIcon className="h-6 w-6" />
          Add Users
        </Button>
      )}
      {noListView}
    </>
  )

  const orderLabel = orderOptions.find((o) => o.value === order)?.label

  const canSendMessage = list?.canSendMessage || list?.type === 'custom'

  const handleClickMessage = () => {
    if (canSendMessage) {
      openDialog({
        type: DIALOG_TYPE.NEW_MESSAGE,
        isPersistent: true,
        defaultIncludedLists: [getListIdToSendMessage(list)],
      })
    }
  }

  let content = noListView

  if (list) {
    content = addingUsers ? (
      <AddUsers
        list={list}
        onFinish={() => setAddingUsers(false)}
      />
    ) : (
      <div className="flex h-full flex-col gap-4">
        <div className="flex justify-between gap-3">
          <div className="flex items-center gap-3">
            <IconButton
              icon={ChevronDownIcon}
              className="rotate-90 lg:hidden"
              onClick={onCloseSelectedList}
            />
            <h3 className="text-[18px] font-semibold leading-[28px]">
              {unescapeHtmlText(list.name)}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {canSendMessage && (
              <IconButton
                tooltip="New Mass Message"
                icon={MessageIcon}
                onClick={handleClickMessage}
              />
            )}
            {list.type === 'custom' && (
              <CollectionActions
                onClickAddUsers={onClickAddUsers}
                collection={list}
                onDelete={onCloseSelectedList}
              />
            )}
          </div>
        </div>
        {list.usersCount > 0 ? (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className="text-secondary-300 text-sm font-medium">
                {orderLabel}
              </p>
              <div className="flex items-center gap-3">
                <TextField
                  placeholder="Search"
                  value={queryValue}
                  className="dark:border-secondary-400 flex-1 resize-none"
                  prefix={<SearchIcon className="text-title" />}
                  onChange={handleSearchChange}
                />
                <CollectionUsersFilters
                  list={list}
                  filters={filters}
                  setFilters={setFilters}
                />
                {list.orderOptions !== null && (
                  <FilterSortOrderOptions
                    sort={order}
                    order={sort}
                    setOrder={setSort}
                    setSort={setOrder}
                    sortOptions={orderOptions}
                    orderOptions={sortOptions}
                  />
                )}
              </div>
            </div>
            <CollectionUsers listId={list.id} />
          </>
        ) : (
          noUsersView
        )}
      </div>
    )
  }

  return (
    <div
      className={classNames(
        'relative max-h-[790px] flex-1 overflow-hidden rounded-lg border p-4 md:min-w-[580px] border-border-secondary',
        className,
      )}
    >
      {content}
    </div>
  )
}
